import React, { useEffect, useState } from "react";
import Icon from "../Shared/Icon";
import { getKavachTransactions } from "../../Actions/Kavach";
import { mapTransactionRow } from "./KavachData";

const PAGE_SIZE = 20;

const COLOR_PRIMARY = "#18406E";
const COLOR_TEXT = "#181818";
const COLOR_MUTED = "#8B8B8B";
const COLOR_BODY = "#585858";
const COLOR_BORDER = "#E8E8E8";
const COLOR_ROW_BORDER = "#EEEEEE";

const KavachTransactionsModal = ({ open, retailerCode, onClose }) => {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [maxKnownPage, setMaxKnownPage] = useState(0);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    if (open) {
      setPage(0);
      setMaxKnownPage(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !retailerCode) return;
    setLoading(true);
    setError(null);
    getKavachTransactions(retailerCode, page, PAGE_SIZE)
      .then((res) => {
        const data = res.data.data || [];
        setRows(data.map(mapTransactionRow));
        const more = data.length === PAGE_SIZE;
        setHasMore(more);
        setMaxKnownPage((m) => Math.max(m, more ? page + 1 : page));
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [open, retailerCode, page, retryTick]);

  if (!open) return null;

  const canGoNext = page < maxKnownPage || (page === maxKnownPage && hasMore);

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
      className="kavach-native-style kavach-modal-overlay">
      <div
        style={styles.modal}
        className="kavach-modal-card"
        onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Transaction History</div>
            <div style={styles.subtitle}>
              Every point &amp; coin earning event on your account
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>
            <Icon name="close" size={16} color={COLOR_MUTED} />
          </button>
        </div>

        <div className="kavach-modal-body" style={styles.tableWrap}>
          {loading && <div style={styles.stateText}>Loading transactions…</div>}

          {!loading && error && (
            <div style={styles.errorState}>
              <span style={styles.stateText}>
                Couldn't load transaction history.
              </span>
              <button
                style={styles.retryBtn}
                onClick={() => setRetryTick((t) => t + 1)}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div style={styles.stateText}>No transactions yet.</div>
          )}

          {!loading && !error && rows.length > 0 && (
            <table className="kavach-txn-table" style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Service</th>
                  <th style={styles.th}>Type</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Amount</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Points</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Coins</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Balance</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={styles.td}>{r.date}</td>
                    <td style={styles.td}>
                      <div style={styles.serviceCell}>
                        <div style={styles.serviceIconWrap}>
                          <Icon name={r.icon} size={13} color={COLOR_PRIMARY} />
                        </div>
                        {r.label}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.entryBadge,
                          ...(r.entryType === "DEBIT"
                            ? styles.entryBadgeDebit
                            : {}),
                        }}>
                        {r.entryType}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      ₹{r.amount.toLocaleString("en-IN")}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "right",
                        fontWeight: "bold",
                      }}>
                      +{r.pointsEarned}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      +{r.coinsEarned.toLocaleString("en-IN")}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      {r.pointsBalance != null ? `${r.pointsBalance} pts` : "—"}
                      <br />
                      <span style={styles.balanceSub}>
                        {r.coinsBalance != null
                          ? `${r.coinsBalance.toLocaleString("en-IN")} coins`
                          : ""}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statusBadge}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={styles.footer}>
          <div style={styles.pagination}>
            <button
              style={{
                ...styles.pageNumBtn,
                ...(page === 0 ? styles.pageNumBtnDisabled : {}),
              }}
              disabled={page === 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}>
              <Icon
                name="chevronLeft"
                size={16}
                strokeWidth={2.2}
                color={page === 0 ? "#A6A6A6" : COLOR_TEXT}
              />
            </button>

            {Array.from({ length: maxKnownPage + 1 }, (_, i) => i).map((i) => (
              <button
                key={i}
                style={{
                  ...styles.pageNumBtn,
                  ...(i === page ? styles.pageNumBtnActive : {}),
                }}
                disabled={loading}
                onClick={() => setPage(i)}>
                {i + 1}
              </button>
            ))}

            <button
              style={{
                ...styles.pageNumBtn,
                ...(!canGoNext ? styles.pageNumBtnDisabled : {}),
              }}
              disabled={!canGoNext || loading}
              onClick={() => setPage((p) => p + 1)}>
              <Icon
                name="chevronRight"
                size={16}
                strokeWidth={2.2}
                color={canGoNext ? COLOR_TEXT : "#A6A6A6"}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(24,24,24,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: 20,
  },
  modal: {
    position: "relative",
    width: 880,
    maxWidth: "100%",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    background: "#FFFFFF",
    border: "1px solid rgba(128,128,128,0.281)",
    borderRadius: 8,
    boxShadow: "0 12px 32px rgba(24,24,24,0.18)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "16px",
    borderBottom: "1px solid rgba(128,128,128,0.123)",
  },
  title: { fontSize: 16, fontWeight: "bold", color: COLOR_PRIMARY },
  subtitle: { fontSize: 12, color: COLOR_MUTED, marginTop: 4 },
  closeBtn: {
    width: 30,
    height: 30,
    minWidth: 30,
    borderRadius: "50%",
    border: "1px solid rgba(128,128,128,0.281)",
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  tableWrap: { flex: 1, overflow: "auto", padding: 16 },
  stateText: { fontSize: 13, color: COLOR_MUTED, padding: "24px 0" },
  errorState: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "24px 0",
  },
  retryBtn: {
    alignSelf: "flex-start",
    border: "none",
    borderRadius: 4,
    padding: "6px 18px",
    background: COLOR_PRIMARY,
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
    cursor: "pointer",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    border: `1px solid ${COLOR_BORDER}`,
  },
  th: {
    textAlign: "left",
    padding: "10px",
    color: COLOR_MUTED,
    fontWeight: "bold",
    fontSize: 12,
    background: COLOR_BORDER,
    position: "sticky",
    top: 0,
  },
  td: {
    padding: "10px",
    color: COLOR_BODY,
    fontSize: 12,
    borderBottom: `1px solid ${COLOR_ROW_BORDER}`,
    whiteSpace: "nowrap",
  },
  serviceCell: { display: "flex", alignItems: "center", gap: 8 },
  serviceIconWrap: {
    width: 24,
    height: 24,
    minWidth: 24,
    borderRadius: 5,
    background: "rgba(24,64,110,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  entryBadge: {
    fontSize: 11,
    fontWeight: "bolder",
    color: "#FFFFFF",
    background: "#7FB28E",
    borderRadius: 4,
    padding: "4px 12px",
  },
  entryBadgeDebit: { background: "#E16689" },
  statusBadge: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#FFFFFF",
    background: "#6F6F6F",
    borderRadius: 3,
    padding: "4px 10px",
  },
  balanceSub: { fontSize: 10.5, color: COLOR_MUTED },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 16px",
    borderTop: "1px solid rgba(128,128,128,0.123)",
  },
  pagination: { display: "flex", alignItems: "center", gap: 8 },
  pageNumBtn: {
    width: 36,
    height: 36,
    minWidth: 36,
    border: "1px solid rgba(128,128,128,0.281)",
    background: "#FFFFFF",
    borderRadius: 6,
    padding: 0,
    fontSize: 12.5,
    fontWeight: "bold",
    color: COLOR_TEXT,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pageNumBtnActive: {
    border: `1px solid ${COLOR_PRIMARY}`,
    background: COLOR_PRIMARY,
    color: "#FFFFFF",
  },
  pageNumBtnDisabled: {
    background: "#FAFAFA",
    cursor: "not-allowed",
  },
};

export default KavachTransactionsModal;
